import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import GUI from 'lil-gui'
import gsap from 'gsap'
import particlesVertexShader from './shaders/particles/vertex.glsl'
import particlesFragmentShader from './shaders/particles/fragment.glsl'
import firefliesVertexShader from './shaders/fireflies/vertex.glsl'
import firefliesFragmentShader from './shaders/fireflies/fragment.glsl'

/**
 * Base
 */
// Debug
const gui = new GUI({ width: 340 })
const debugObject = {}

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

// Loaders
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: Math.min(window.devicePixelRatio, 2)
}

window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    sizes.pixelRatio = Math.min(window.devicePixelRatio, 2)

    // Materials
    if (particles)
        particles.material.uniforms.uResolution.value.set(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(sizes.pixelRatio)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 0, 8 * 2)
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
})

renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(sizes.pixelRatio)

debugObject.clearColor = '#212121'
gui.addColor(debugObject, 'clearColor').onChange(() => { renderer.setClearColor(debugObject.clearColor) })
renderer.setClearColor(debugObject.clearColor)

/**
 * raycaster
 */
const raycaster = new THREE.Raycaster()
const pointer = new THREE.Vector2()
const point = new THREE.Vector3()
// let mesh = new THREE.Mesh(
//     new THREE.PlaneGeometry(10, 10, 10, 10).rotateX(-Math.PI / 2),
//     new THREE.MeshBasicMaterial({ color: 'red', wireframe: true })
// )
// scene.add(mesh)

// window.addEventListener('pointermove', (event) => {
//     pointer.x = (event.clientX / sizes.width) * 2 - 1
//     pointer.y = -(event.clientY / sizes.height) * 2 + 1

//     raycaster.setFromCamera(pointer, camera)
//     const intersects = raycaster.intersectObjects([mesh])
//     console.log(intersects)
//     if (intersects.length > 0) {
//         console.log(intersects[0].point)
//     }
// })

window.addEventListener('pointermove', (event) => {
    pointer.x = (event.clientX / sizes.width) * 2 - 1
    pointer.y = -(event.clientY / sizes.height) * 2 + 1

    // 将屏幕坐标转换为世界坐标
    const vector = new THREE.Vector3(pointer.x, pointer.y, 0)
    vector.unproject(camera)
    point.copy(vector)

    // 如果有相交点，使用相交点位置
    if (particles && particles.points) {
        raycaster.setFromCamera(pointer, camera)
        const intersects = raycaster.intersectObject(particles.points)
        if (intersects.length > 0) {
            point.copy(intersects[0].point)
            console.log(point)
        }
    }
})

/**
 * Particles
 */
let particles = null

gltfLoader.load('./models.glb', (gltf) => {
    particles = {}
    particles.index = 0

    // Positions
    const positions = gltf.scene.children.map(child => child.geometry.attributes.position)

    particles.maxCount = 0
    for (const position of positions) {
        if (position.count > particles.maxCount)
            particles.maxCount = position.count
    }

    particles.positions = []
    for (const position of positions) {
        const originalArray = position.array
        const newArray = new Float32Array(particles.maxCount * 3)

        for (let i = 0; i < particles.maxCount; i++) {
            const i3 = i * 3

            if (i3 < originalArray.length) {
                newArray[i3 + 0] = originalArray[i3 + 0]
                newArray[i3 + 1] = originalArray[i3 + 1]
                newArray[i3 + 2] = originalArray[i3 + 2]
            }
            else {
                const randomIndex = Math.floor(position.count * Math.random()) * 3
                newArray[i3 + 0] = originalArray[randomIndex + 0]
                newArray[i3 + 1] = originalArray[randomIndex + 1]
                newArray[i3 + 2] = originalArray[randomIndex + 2]
            }
        }

        particles.positions.push(new THREE.Float32BufferAttribute(newArray, 3))
    }

    // Geometry 
    const sizesArray = new Float32Array(particles.maxCount)

    for (let i = 0; i < particles.maxCount; i++)
        sizesArray[i] = Math.random()

    particles.geometry = new THREE.BufferGeometry()
    particles.geometry.setAttribute('position', particles.positions[particles.index])
    particles.geometry.setAttribute('aPositionTarget', particles.positions[3])
    particles.geometry.setAttribute('aSize', new THREE.BufferAttribute(sizesArray, 1))


    // Material
    particles.colorA = '#b8fff7'
    particles.colorB = '#0062ff'

    particles.material = new THREE.ShaderMaterial({
        vertexShader: particlesVertexShader,
        fragmentShader: particlesFragmentShader,
        uniforms:
        {
            uTime: new THREE.Uniform(0),
            uSize: new THREE.Uniform(0.4),
            uResolution: new THREE.Uniform(new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)),
            uProgress: new THREE.Uniform(0),
            uColorA: new THREE.Uniform(new THREE.Color(particles.colorA)),
            uColorB: new THREE.Uniform(new THREE.Color(particles.colorB)),
            uMouse: new THREE.Uniform(new THREE.Vector3(point))
        },
        blending: THREE.AdditiveBlending,
        depthWrite: false
    })

    // Points
    particles.points = new THREE.Points(particles.geometry, particles.material)
    particles.points.frustumCulled = false
    scene.add(particles.points)

    // Methods
    particles.morph = (index) => {
        // Update attributes
        particles.geometry.attributes.position = particles.positions[particles.index]
        particles.geometry.attributes.aPositionTarget = particles.positions[index]

        // Animate uProgress
        gsap.fromTo(
            particles.material.uniforms.uProgress,
            { value: 0 },
            { value: 1, duration: 3, ease: 'linear' }
        )

        // Save index
        particles.index = index
    }

    // 添加自动过渡的timeline
    particles.autoMorph = () => {
        const timeline = gsap.timeline({
            repeat: -1, // 无限循环
            onUpdate: () => {
                // 更新GUI显示
                gui.controllers.forEach(controller => controller.updateDisplay())
            }
        });

        // 初始状态
        timeline.set(particles.material.uniforms.uProgress, { value: 0 });

        // 添加各个过渡阶段
        for (let i = 0; i <= 2; i++) {
            // 过渡到下一个形状
            timeline.add(() => {
                particles.geometry.attributes.position = particles.positions[i];
                particles.geometry.attributes.aPositionTarget = particles.positions[(i + 1) % 3];
                particles.index = i;
            });

            // 动画过渡
            timeline.to(particles.material.uniforms.uProgress, {
                value: 1,
                duration: 3,
                ease: 'linear'
            });

            // 暂停2秒
            timeline.to({}, { duration: 2 });

            // 重置progress
            timeline.set(particles.material.uniforms.uProgress, { value: 0 });
        }

        return timeline;
    }

    // 创建并启动timeline
    particles.timeline = particles.autoMorph();

    // Tweaks
    gui.addColor(particles, 'colorA').onChange(() => {
        particles.material.uniforms.uColorA.value.set(particles.colorA)
        firefliesMaterial.uniforms.uColorA.value.set(value)
    })
    gui.addColor(particles, 'colorB').onChange(() => {
        particles.material.uniforms.uColorB.value.set(particles.colorB)
        firefliesMaterial.uniforms.uColorB.value.set(value)
    })
    // gui.add(particles.material.uniforms.uProgress, 'value').min(0).max(1).step(0.001).name('uProgress').listen()

    particles.morph0 = () => { particles.morph(0) }
    particles.morph1 = () => { particles.morph(1) }
    particles.morph2 = () => { particles.morph(2) }
    particles.morph3 = () => { particles.morph(3) }

    // gui.add(particles, 'morph0')
    // gui.add(particles, 'morph1')
    // gui.add(particles, 'morph2')
    // gui.add(particles, 'morph3')
})

/**
 * Fireflies
 */
const firefliesCount = 250
const firefliesGeometry = new THREE.BufferGeometry()

const positionsArray = new Float32Array(firefliesCount * 3)
const scalesArray = new Float32Array(firefliesCount)
const speedsArray = new Float32Array(firefliesCount * 3)

for (let i = 0; i < firefliesCount; i++) {
    // Position
    positionsArray[i * 3] = (Math.random() - 0.5) * 10
    positionsArray[i * 3 + 1] = (Math.random() - 0.5) * 10
    positionsArray[i * 3 + 2] = (Math.random() - 0.5) * 10

    // Scale
    scalesArray[i] = Math.random() * 0.5 + 0.5

    // Speed (减小速度范围)
    speedsArray[i * 3] = (Math.random() - 0.5) * 0.05
    speedsArray[i * 3 + 1] = (Math.random() - 0.5) * 0.05
    speedsArray[i * 3 + 2] = (Math.random() - 0.5) * 0.05
}

firefliesGeometry.setAttribute('position', new THREE.BufferAttribute(positionsArray, 3))
firefliesGeometry.setAttribute('aScale', new THREE.BufferAttribute(scalesArray, 1))
firefliesGeometry.setAttribute('aSpeed', new THREE.BufferAttribute(speedsArray, 3))

const firefliesMaterial = new THREE.ShaderMaterial({
    vertexShader: firefliesVertexShader,
    fragmentShader: firefliesFragmentShader,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    uniforms: {
        uTime: { value: 0 },
        uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
        uSize: { value: 500 },
        uColorA: { value: new THREE.Color('#b8fff7') },
        uColorB: { value: new THREE.Color('#0062ff') },
        uColorMix: { value: 0 }
    }
})

const fireflies = new THREE.Points(firefliesGeometry, firefliesMaterial)
scene.add(fireflies)

// Fireflies GUI
const firefliesFolder = gui.addFolder('Fireflies')
firefliesFolder.add(firefliesMaterial.uniforms.uSize, 'value').min(0).max(1500).step(1).name('Size')
firefliesFolder.add({ count: firefliesCount }, 'count').min(0).max(1500).step(1).name('Count').onChange((value) => {
    // Update geometry
    const newPositionsArray = new Float32Array(value * 3)
    const newScalesArray = new Float32Array(value)
    const newSpeedsArray = new Float32Array(value * 3)

    for (let i = 0; i < value; i++) {
        // Position
        newPositionsArray[i * 3] = (Math.random() - 0.5) * 10
        newPositionsArray[i * 3 + 1] = (Math.random() - 0.5) * 10
        newPositionsArray[i * 3 + 2] = (Math.random() - 0.5) * 10

        // Scale
        newScalesArray[i] = Math.random() * 0.5 + 0.5

        // Speed (减小速度范围)
        newSpeedsArray[i * 3] = (Math.random() - 0.5) * 0.05
        newSpeedsArray[i * 3 + 1] = (Math.random() - 0.5) * 0.05
        newSpeedsArray[i * 3 + 2] = (Math.random() - 0.5) * 0.05
    }

    firefliesGeometry.setAttribute('position', new THREE.BufferAttribute(newPositionsArray, 3))
    firefliesGeometry.setAttribute('aScale', new THREE.BufferAttribute(newScalesArray, 1))
    firefliesGeometry.setAttribute('aSpeed', new THREE.BufferAttribute(newSpeedsArray, 3))
})

/**
 * Animate
 */
const clock = new THREE.Clock()
const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Update fireflies
    const positions = fireflies.geometry.attributes.position.array
    const speeds = fireflies.geometry.attributes.aSpeed.array

    for (let i = 0; i < firefliesCount; i++) {
        const i3 = i * 3

        // Update position based on speed (减小时间系数)
        positions[i3] += speeds[i3] * Math.sin(elapsedTime * 0.2)
        positions[i3 + 1] += speeds[i3 + 1] * Math.cos(elapsedTime * 0.2)
        positions[i3 + 2] += speeds[i3 + 2] * Math.sin(elapsedTime * 0.1)

        // Boundary check
        if (Math.abs(positions[i3]) > 5) speeds[i3] *= -1
        if (Math.abs(positions[i3 + 1]) > 5) speeds[i3 + 1] *= -1
        if (Math.abs(positions[i3 + 2]) > 5) speeds[i3 + 2] *= -1
    }

    fireflies.geometry.attributes.position.needsUpdate = true
    firefliesMaterial.uniforms.uTime.value = elapsedTime
    firefliesMaterial.uniforms.uColorMix.value = (Math.sin(elapsedTime) + 1) * 0.5

    //Update material
    if (particles) {
        particles.material.uniforms.uTime.value = elapsedTime
        particles.material.uniforms.uMouse.value.copy(point)
    }


    // Update controls
    controls.update()

    // Render normal scene
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()